import type { FC } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import type { AppState } from 'index';

import { getTestIdByParts } from 'utils/getTestIdByParts';
import type { Noop, WithTestId } from 'types/app';

import { getOfferTranslation } from '@Modules/offers/slice';

import { ShowMoreButton } from '@Atomic/atoms/ShowMoreButton';
import { Text } from '@Atomic/atoms/Text';
import { TranslateButton } from '@Atomic/atoms/TranslateButton';

import styles from './style.module.scss';

type Props = {
  className?: string;
  description?: string;
  showTranslation: boolean;
  translationAvailable: boolean;
  onTranslate: Noop;
  onShowOriginal: Noop;
};

const MAX_VISIBLE_SYMBOLS = 245;

export const OfferDescription: FC<WithTestId<Props>> = ({
  className,
  testId,
  description = '',
  translationAvailable,
  showTranslation,
  onTranslate,
  onShowOriginal,
}) => {
  const { t } = useTranslation();
  const [showFullDescription, setShowFullDescription] = useState(false);
  const isTranslating = useSelector((state: AppState) => state.loading[getOfferTranslation.name]);

  const shouldRenderShowMoreButton = description.length > MAX_VISIBLE_SYMBOLS;
  const truncatedText =
    description.slice(0, MAX_VISIBLE_SYMBOLS) +
    (description.length > MAX_VISIBLE_SYMBOLS ? '...' : '');

  return (
    <div className={className} data-testid={testId}>
      <Text className={styles.description}>
        {showFullDescription ? description : truncatedText}
      </Text>

      {showTranslation && (
        <div className={styles.translationWarning}>{t('offer.translationWarn')}</div>
      )}

      {shouldRenderShowMoreButton || translationAvailable ? (
        <div className={styles.descriptionButtons}>
          {shouldRenderShowMoreButton && (
            <ShowMoreButton
              size="small"
              isToggled={showFullDescription}
              onClick={() => setShowFullDescription(!showFullDescription)}
              testId={getTestIdByParts(testId, 'more')}
            />
          )}

          {translationAvailable && (
            <TranslateButton
              loading={isTranslating}
              isTranslationShown={showTranslation}
              onShowOriginal={onShowOriginal}
              onTranslate={onTranslate}
              size="small"
              testId={getTestIdByParts(testId, showTranslation ? 'show-original' : 'translate')}
            />
          )}
        </div>
      ) : null}
    </div>
  );
};
